import { Component, Input, SimpleChanges } from '@angular/core';
import { DefaultGraphStyle, DeviationGraphChannel, ExhaustGraphChannel } from '@models/graphs';
import { GraphsService } from '@service/graphs.service';
import { Channel } from 'ops/common/models/channel';
import { ECharts, EChartsOption } from 'echarts';
import { BarChart } from 'echarts/charts';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';

@Component({
    selector: 'app-cylinder-deviation',
    templateUrl: './cylinder-deviation.component.html',
    styleUrls: ['./cylinder-deviation.component.css'],
    standalone: false
})
export class CylinderDeviationComponent {
    @Input() devaitons: DeviationGraphChannel[];
    @Input() deviationLimit: number;
    @Input() averageChannel: Channel;
    @Input() averageChannelValue = 0;
    @Input() title: string;

    readonly echartsExtentions: any[];
    echartsOptions: EChartsOption = {};
    chart: ECharts = null;

    constructor(private graphService: GraphsService) {
        this.echartsExtentions = [BarChart, TooltipComponent, GridComponent, LegendComponent];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.chart) {
            this.updateChartOption();
        }
    }

    ngOnInit(): void {
        this.initEchartOption();
    }

    updateChartOption() {
        let newseries = this.echartsOptions;
        newseries.xAxis[0].data = this.graphService.generateTitleArr(this.devaitons);
        newseries.xAxis[1].data = this.graphService.generateValueArr(this.devaitons);
        newseries.yAxis[0].max = Math.abs(this.deviationLimit);
        newseries.yAxis[0].min = -Math.abs(this.deviationLimit);
        newseries.yAxis[0].interval = Math.abs(this.deviationLimit) / 2;
        newseries.series[0].data = this.generateSeriesValueArr();

        this.echartsOptions = newseries;
        this.chart.setOption(this.echartsOptions);
    }

    initEchartOption() {
        this.echartsOptions = {
            width: '95%',
            color: [DefaultGraphStyle.blueColor],
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
                backgroundColor: DefaultGraphStyle.tooltipBgColor,
                borderColor: DefaultGraphStyle.tooltipBorderColor,
                formatter: (params) => {
                    let result = '';
                    params.forEach((item) => {
                        result +=
                            this.graphService.getTootipBlueText(item.name) +
                            this.graphService.getTootipSubname(
                                'Temperature',
                                this.graphService.getTootipBlueTextBold(
                                    item.data.temperatureChannel,
                                ),
                            ) +
                            this.graphService.getTootipSubname(
                                'Deviation',
                                this.graphService.getTootipBlueTextBold(item.data.channel),
                            ) +
                            this.graphService.getTootipWhiteText(`${item.value}&#8451`);
                    });
                    return result;
                },
                padding: 8,
            },
            xAxis: [
                {
                    type: 'category',
                    data: this.graphService.generateTitleArr(this.devaitons),
                    axisPointer: {
                        type: 'shadow',
                    },
                    position: 'bottom',
                    axisTick: {
                        show: false,
                    },
                },
                {
                    type: 'category',
                    data: this.graphService.generateValueArr(this.devaitons),
                    axisPointer: {
                        type: 'shadow',
                    },
                    position: 'bottom',
                    offset: 30,
                    axisLine: {
                        show: false,
                    },
                    axisTick: {
                        show: false,
                    },
                },
            ],
            yAxis: [
                {
                    type: 'value',
                    max: Math.abs(this.deviationLimit),
                    min: -Math.abs(this.deviationLimit),
                    interval: Math.abs(this.deviationLimit) / 2,
                    axisLine: {
                        lineStyle: {
                            color: DefaultGraphStyle.yAxisColor,
                        },
                    },
                    splitLine: {
                        lineStyle: {
                            color: DefaultGraphStyle.yAxisColor,
                        },
                    },
                    axisLabel: {
                        fontSize: DefaultGraphStyle.fontSize,
                        formatter: (value) => {
                            return value.toFixed();
                        },
                    },
                },
            ],
            grid: {
                left: '5%',
                tooltip: {
                    axisPointer: {
                        shadowStyle: {
                            color: DefaultGraphStyle.hoverBgColor,
                        },
                    },
                },
            },
            series: [
                {
                    data: this.generateSeriesValueArr(),
                    type: 'bar',
                    emphasis: {
                        itemStyle: {
                            color: DefaultGraphStyle.hoverColor,
                        },
                    },
                    barMaxWidth: 100,
                },
            ],
        };
    }

    onChartInit(event: ECharts) {
        this.chart = event;
    }

    generateSeriesValueArr() {
        let values = [];
        for (let i = 0; i < this.devaitons.length; i++) {
            values[i] = {
                value: this.devaitons[i].value,
                channel: this.devaitons[i].channel.opsData.name,
                temperatureChannel: this.devaitons[i].temperatureChannel.opsData.name,
            };
        }

        return values;
    }
}
